import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { checkIn, checkOut, listEvents } from "@/features/shift-events/api/shiftEventsApi";

export function useShiftEvents(shiftId: string | undefined) {
  return useQuery({
    queryKey: ["shift-events", shiftId],
    queryFn: () => listEvents(shiftId!),
    enabled: !!shiftId,
  });
}

type GpsReading = {
  gps_lat: number | null;
  gps_lng: number | null;
  gps_accuracy: number | null;
  denialReason?: "unsupported" | "denied" | "timeout" | "unavailable";
};

function getGps(): Promise<GpsReading> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ gps_lat: null, gps_lng: null, gps_accuracy: null, denialReason: "unsupported" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          gps_lat: pos.coords.latitude,
          gps_lng: pos.coords.longitude,
          gps_accuracy: pos.coords.accuracy,
        }),
      (err) => {
        const reason: GpsReading["denialReason"] =
          err.code === err.PERMISSION_DENIED
            ? "denied"
            : err.code === err.TIMEOUT
              ? "timeout"
              : "unavailable";
        resolve({ gps_lat: null, gps_lng: null, gps_accuracy: null, denialReason: reason });
      },
      // Strict options: require a fresh, high-accuracy fix.
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

function mutationError(reading: GpsReading): Error | null {
  if (reading.denialReason === "denied") {
    return new Error(
      "Location access blocked. Allow location in your browser settings to check in.",
    );
  }
  if (reading.denialReason === "timeout") {
    return new Error("Couldn't get a GPS fix in time. Move to an open area and try again.");
  }
  if (reading.denialReason === "unavailable") {
    return new Error("Your device couldn't determine its location. Try again.");
  }
  if (reading.denialReason === "unsupported") {
    return new Error("This browser doesn't support location. Open the app on a modern browser.");
  }
  return null;
}

type GpsOverride = { gps_lat: number; gps_lng: number };

export function useCheckIn(shiftId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (override?: GpsOverride) => {
      if (override) {
        return checkIn(shiftId, { ...override, gps_accuracy: 1 });
      }
      const reading = await getGps();
      const err = mutationError(reading);
      if (err) throw err;
      return checkIn(shiftId, reading);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shift-events", shiftId] });
      qc.invalidateQueries({ queryKey: ["shifts"] });
      // Refresh worker's application status so the ApplyButton no longer
      // shows stale "Application pending" after a check-in succeeds.
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
  });
}

export function useCheckOut(shiftId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (override?: GpsOverride) => {
      if (override) {
        return checkOut(shiftId, { ...override, gps_accuracy: 1 });
      }
      const reading = await getGps();
      const err = mutationError(reading);
      if (err) throw err;
      return checkOut(shiftId, reading);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shift-events", shiftId] });
      qc.invalidateQueries({ queryKey: ["shifts"] });
      qc.invalidateQueries({ queryKey: ["applications"] });
      // Check-out creates a timesheet row + finalizes earnings; the rating
      // form unlocks once the shift is COMPLETED.
      qc.invalidateQueries({ queryKey: ["timesheets"] });
      qc.invalidateQueries({ queryKey: ["earnings"] });
      qc.invalidateQueries({ queryKey: ["ratings", shiftId] });
      // Ops side: a freshly-completed shift drops into the payout queue and
      // the upcoming-shifts dashboard loses one row.
      qc.invalidateQueries({ queryKey: ["ops", "payouts"] });
      qc.invalidateQueries({ queryKey: ["ops", "upcoming-shifts"] });
    },
  });
}
